import {
    AiAssistantConversationContext,
    AiAssistantConversations,
    ChatType,
    HibuAssistantMessage,
    Role,
} from '../common/types';
import {SearchRequest} from '../../search/search-types';
import {Dispatch, GetState, TempThunkAction} from '../../app-types';
import {ChatConversationDto, ChatHistoryService, CreateConversationRequest} from '../../services/chat-history-service';
import {showError} from '../../message-box/message-box-actions';
import {SearchPageType} from '../../search/search-config';

export const ASSISTANT_ACTION_TYPES = {
    SHOW_CHAT_SUGGESTIONS: 'SHOW_CHAT_SUGGESTIONS',
    SET_SUGGESTIONS: 'SET_SUGGESTIONS',
    SET_TOTAL_DUPLICATES: 'SET_TOTAL_DUPLICATES',
    ADD_SUMMARY: 'ADD_SUMMARY',
    UPDATE_CONVERSATION_CONTEXT: 'UPDATE_CONVERSATION_CONTEXT',
    LOAD_CONVERSATIONS: 'LOAD_CONVERSATIONS',
    LOAD_CONVERSATIONS_ERROR: 'LOAD_CONVERSATIONS_ERROR',
    LOAD_CONVERSATIONS_LOADING: 'LOAD_CONVERSATIONS_LOADING',
    START_LOADING_CONVERSATION: 'START_LOADING_CONVERSATION',
    FINISH_LOADING_CONVERSATION: 'FINISH_LOADING_CONVERSATION',
    CREATE_CONVERSATION_SUCCESS: 'CREATE_CONVERSATION_SUCCESS',
    ADD_MESSAGE_SUCCESS: 'ADD_MESSAGE_SUCCESS',
    DELETE_CONVERSATION_SUCCESS: 'DELETE_CONVERSATION_SUCCESS',
} as const;

type ShowChatSuggestionsAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.SHOW_CHAT_SUGGESTIONS;
    showChatSuggestions: boolean;
};

type SetSuggestionsAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.SET_SUGGESTIONS;
    conversationId: string;
    suggestions: string[];
};

type SetTotalDuplicatesAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.SET_TOTAL_DUPLICATES;
    conversationId: string;
    totalDuplicates: number;
};

type AddSummaryAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.ADD_SUMMARY;
    conversationId: string;
    summary: string;
};

type UpdateConversationContextAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.UPDATE_CONVERSATION_CONTEXT;
    conversationId: string;
    context: AiAssistantConversationContext;
};

type LoadConversationsAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.LOAD_CONVERSATIONS;
    conversations: AiAssistantConversations;
};

type LoadConversationsErrorAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.LOAD_CONVERSATIONS_ERROR;
    error: string;
};

type LoadConversationsLoadingAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.LOAD_CONVERSATIONS_LOADING;
};

type StartLoadingConversationAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.START_LOADING_CONVERSATION;
    conversationId: string;
};

type FinishLoadingConversationAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.FINISH_LOADING_CONVERSATION;
    conversationId: string;
};

type CreateConversationSuccessAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.CREATE_CONVERSATION_SUCCESS;
    conversationId: string;
    message: string;
    chatType: ChatType;
    role: Role.ASSISTANT | Role.USER;
    searchRequest?: SearchRequest;
};

type AddMessageSuccessAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.ADD_MESSAGE_SUCCESS;
    conversationId: string;
    newMessage: HibuAssistantMessage;
    searchRequest?: SearchRequest;
};

type DeleteConversationSuccessAction = {
    readonly type: typeof ASSISTANT_ACTION_TYPES.DELETE_CONVERSATION_SUCCESS;
    conversationId: string;
};

export type AssistantAction =
    | ShowChatSuggestionsAction
    | AddSummaryAction
    | SetSuggestionsAction
    | SetTotalDuplicatesAction
    | UpdateConversationContextAction
    | LoadConversationsAction
    | LoadConversationsErrorAction
    | LoadConversationsLoadingAction
    | StartLoadingConversationAction
    | FinishLoadingConversationAction
    | CreateConversationSuccessAction
    | AddMessageSuccessAction
    | DeleteConversationSuccessAction;

export function changeShowChatSuggestionsAction(showChatSuggestions: boolean): ShowChatSuggestionsAction {
    return {
        type: ASSISTANT_ACTION_TYPES.SHOW_CHAT_SUGGESTIONS,
        showChatSuggestions: showChatSuggestions,
    };
}

export function setSuggestionsAction(conversationId: string, suggestions: string[]): SetSuggestionsAction {
    return {
        type: ASSISTANT_ACTION_TYPES.SET_SUGGESTIONS,
        conversationId: conversationId,
        suggestions: suggestions,
    };
}

export function setTotalDuplicatesAction(conversationId: string, totalDuplicates: number): SetTotalDuplicatesAction {
    return {
        type: ASSISTANT_ACTION_TYPES.SET_TOTAL_DUPLICATES,
        conversationId: conversationId,
        totalDuplicates: totalDuplicates,
    };
}

function getMessageBoxId(chatType: ChatType): string {
    if (chatType === ChatType.GENERAL || chatType === ChatType.MULTI) {
        return chatType;
    }
    return SearchPageType.BASIC;
}

/**
 * Update suggestions for a conversation and persist to DB
 */
export const doUpdateSuggestionsRequest = (conversationId: string, suggestions: string[]): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const conversation = state.assistant.conversations[conversationId];

        if (!conversation) {
            console.warn(`Conversation ${conversationId} not found for suggestions update`);
            return;
        }

        try {
            const conversationDto = ChatHistoryService.toConversationDto(conversation);
            await ChatHistoryService.updateConversation(conversationId, {
                ...conversationDto,
                suggestions: JSON.stringify(suggestions),
            });
            dispatch(setSuggestionsAction(conversationId, suggestions));
        } catch (error) {
            console.error('Failed to update suggestions:', error);
            dispatch(setSuggestionsAction(conversationId, suggestions));
            const messageBoxId = getMessageBoxId(conversation.type);
            dispatch(showError(messageBoxId, 'assistant.error.updateSuggestions'));
        }
    };
};

export function addSummaryAction(conversationId: string, summary: string): AddSummaryAction {
    return {
        type: ASSISTANT_ACTION_TYPES.ADD_SUMMARY,
        conversationId: conversationId,
        summary: summary,
    };
}

/**
 * Update summary for a conversation and persist to DB
 */
export const doUpdateSummaryRequest = (conversationId: string, summary: string): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const conversation = state.assistant.conversations[conversationId];

        if (!conversation) {
            console.warn(`Conversation ${conversationId} not found for summary update`);
            return;
        }

        try {
            const conversationDto = ChatHistoryService.toConversationDto(conversation);
            await ChatHistoryService.updateConversation(conversationId, {
                ...conversationDto,
                summary: summary,
            });
            dispatch(addSummaryAction(conversationId, summary));
        } catch (error) {
            console.error('Failed to update summary:', error);
            dispatch(addSummaryAction(conversationId, summary));
            const messageBoxId = getMessageBoxId(conversation.type);
            dispatch(showError(messageBoxId, 'assistant.error.updateSummary'));
        }
    };
};

/**
 * Update totalDuplicates for a conversation and persist to DB
 */
export const doUpdateTotalDuplicatesRequest = (conversationId: string, totalDuplicatesCount: number): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const conversation = state.assistant.conversations[conversationId];

        if (!conversation) {
            console.warn(`Conversation ${conversationId} not found for total duplicates update`);
            return;
        }

        try {
            const conversationDto: ChatConversationDto = ChatHistoryService.toConversationDto(conversation);
            await ChatHistoryService.updateConversation(conversationId, {
                ...conversationDto,
                totalDuplicatesCount: totalDuplicatesCount,
            });
            dispatch(setTotalDuplicatesAction(conversationId, totalDuplicatesCount));
        } catch (error) {
            console.error('Failed to update total duplicates:', error);
            dispatch(setTotalDuplicatesAction(conversationId, totalDuplicatesCount));
        }
    };
};


export function updateConversationContextAction(
    conversationId: string,
    context: AiAssistantConversationContext,
): UpdateConversationContextAction {
    return {
        type: ASSISTANT_ACTION_TYPES.UPDATE_CONVERSATION_CONTEXT,
        conversationId,
        context,
    };
}

export function loadConversationsAction(conversations: AiAssistantConversations) {
    return {
        type: ASSISTANT_ACTION_TYPES.LOAD_CONVERSATIONS,
        conversations: conversations,
    };
}

export function loadConversationsErrorAction(error: string) {
    return {
        type: ASSISTANT_ACTION_TYPES.LOAD_CONVERSATIONS_ERROR,
        error: error,
    };
}

export function loadConversationsLoadingAction() {
    return {
        type: ASSISTANT_ACTION_TYPES.LOAD_CONVERSATIONS_LOADING,
    };
}

export function startLoadingConversationAction(conversationId: string): StartLoadingConversationAction {
    return {
        type: ASSISTANT_ACTION_TYPES.START_LOADING_CONVERSATION,
        conversationId,
    };
}

export function finishLoadingConversationAction(conversationId: string): FinishLoadingConversationAction {
    return {
        type: ASSISTANT_ACTION_TYPES.FINISH_LOADING_CONVERSATION,
        conversationId,
    };
}

export function createConversationSuccessAction(
    conversationId: string,
    message: string,
    chatType: ChatType,
    role: Role.ASSISTANT | Role.USER,
    searchRequest?: SearchRequest,
): CreateConversationSuccessAction {
    return {
        type: ASSISTANT_ACTION_TYPES.CREATE_CONVERSATION_SUCCESS,
        conversationId,
        message,
        chatType,
        role,
        searchRequest,
    };
}

export function addMessageSuccessAction(
    conversationId: string,
    newMessage: HibuAssistantMessage,
    searchRequest?: SearchRequest,
): AddMessageSuccessAction {
    return {
        type: ASSISTANT_ACTION_TYPES.ADD_MESSAGE_SUCCESS,
        conversationId,
        newMessage,
        searchRequest,
    };
}

export function deleteConversationSuccessAction(conversationId: string): DeleteConversationSuccessAction {
    return {
        type: ASSISTANT_ACTION_TYPES.DELETE_CONVERSATION_SUCCESS,
        conversationId,
    };
}

/**
 * Load all conversations of a specific chat type (for GENERAL and MULTI types)
 * @param chatType The chat type to load conversations for
 */
export const doLoadConversationsRequest = (chatType: ChatType): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        dispatch(loadConversationsLoadingAction());
        try {
            const conversationDtos = await ChatHistoryService.getConversations(chatType);
            const conversations: AiAssistantConversations = {};
            await Promise.all(
                conversationDtos.map(async (dto) => {
                    const conversation = ChatHistoryService.toAiAssistantConversation(dto);
                    try {
                        const messageDtos = await ChatHistoryService.getMessages(dto.conversationId);
                        conversation.conversationMessages = messageDtos.map((msgDto) =>
                            ChatHistoryService.toHibuAssistantMessage(msgDto),
                        );
                    } catch (error) {
                        console.error(`Failed to load messages for conversation ${dto.conversationId}:`, error);
                        conversation.conversationMessages = [];
                    }
                    conversations[conversation.id] = conversation;
                }),
            );
            dispatch(loadConversationsAction(conversations));
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load conversations';
            dispatch(loadConversationsErrorAction(errorMessage));
            dispatch(showError(chatType, 'assistant.error.loadConversations'));
        }
    };
};

/**
 * Load a single conversation by ID (for SEARCH and SINGLE types).
 * Uses Redux state to track loading status and prevent duplicate operations.
 *
 * @param conversationId The conversation ID to load
 */
export const doLoadConversationRequest = (conversationId: string): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();

        // Skip if already loaded
        if (state.assistant.conversations[conversationId]) {
            return;
        }

        // Skip if currently being loaded or created
        if (state.assistant.loadingConversationIds.has(conversationId)) {
            return;
        }

        // Mark as loading
        dispatch(startLoadingConversationAction(conversationId));

        try {
            const conversationDto = await ChatHistoryService.getConversation(conversationId);
            if (!conversationDto) {
                // Not found in DB - mark as finished to allow creation
                dispatch(finishLoadingConversationAction(conversationId));
                return;
            }

            const conversation = ChatHistoryService.toAiAssistantConversation(conversationDto);
            try {
                const messageDtos = await ChatHistoryService.getMessages(conversationId);
                conversation.conversationMessages = messageDtos.map((msgDto) =>
                    ChatHistoryService.toHibuAssistantMessage(msgDto),
                );
            } catch (error) {
                console.error(`Failed to load messages for conversation ${conversationId}:`, error);
                conversation.conversationMessages = [];
            }

            const conversations: AiAssistantConversations = {
                ...state.assistant.conversations,
                [conversation.id]: conversation,
            };
            dispatch(loadConversationsAction(conversations));
            dispatch(finishLoadingConversationAction(conversationId));
        } catch (error) {
            console.error(`Failed to load conversation ${conversationId}:`, error);
            dispatch(finishLoadingConversationAction(conversationId));
            // This function is only used for SEARCH and SINGLE types (embedded in search page)
            dispatch(showError(SearchPageType.BASIC, 'assistant.error.loadConversation'));
        }
    };
};

export const doCreateConversationRequest = (
    chatType: ChatType,
    conversationId: string,
    message: string,
    role: Role.ASSISTANT | Role.USER,
    searchRequest?: SearchRequest,
): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();

        if (state.assistant.conversations[conversationId]) {
            return;
        }

        // Skip if currently being loaded or created
        if (state.assistant.loadingConversationIds.has(conversationId)) {
            return;
        }

        // Mark as loading (creating)
        dispatch(startLoadingConversationAction(conversationId));

        try {

            const createRequest: CreateConversationRequest = {
                conversationId,
                chatType,
                searchResultsRequests: JSON.stringify(searchRequest ? [searchRequest] : []),
                suggestions: JSON.stringify([]),
                summary: '',
                contextSettings: JSON.stringify({enableDocuments: true, enableWebSearch: false, tags: []}),
            };

            await ChatHistoryService.createConversation(createRequest);
            await ChatHistoryService.addMessage(conversationId, {
                text: message,
                role: role,
            });
            dispatch(
                createConversationSuccessAction(
                    conversationId,
                    message,
                    chatType,
                    role,
                    searchRequest,
                ),
            );
        } catch (error) {
            console.error('[doCreateConversationRequest] ERROR:', error);
            dispatch(
                createConversationSuccessAction(
                    conversationId,
                    message,
                    chatType,
                    role,
                    searchRequest,
                ),
            );
            const messageBoxId = getMessageBoxId(chatType);
            dispatch(showError(messageBoxId, 'assistant.error.createConversation'));
        } finally {
            dispatch(finishLoadingConversationAction(conversationId));
        }
    };
};

export const doAddMessageRequest = (
    conversationId: string,
    newMessage: HibuAssistantMessage,
    searchRequest?: SearchRequest,
): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const conversation = state.assistant.conversations[conversationId];

        try {
            await ChatHistoryService.addMessage(conversationId, ChatHistoryService.toAddMessageRequest(newMessage));
            dispatch(addMessageSuccessAction(conversationId, newMessage, searchRequest));
        } catch (error) {
            console.error('[doAddMessageRequest] Failed to add message:', error);
            dispatch(addMessageSuccessAction(conversationId, newMessage, searchRequest));
            if (conversation) {
                const messageBoxId = getMessageBoxId(conversation.type);
                dispatch(showError(messageBoxId, 'assistant.error.addMessage'));
            }
        }
    };
};

export const doDeleteConversationRequest = (conversationId: string): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        console.log('[doDeleteConversationRequest] Deleting conversation:', conversationId);
        const state = getState();
        const conversation = state.assistant.conversations[conversationId];

        try {
            await ChatHistoryService.deleteConversation(conversationId);
            console.log('[doDeleteConversationRequest] Successfully deleted from DB');
            dispatch(deleteConversationSuccessAction(conversationId));
        } catch (error) {
            console.error('[doDeleteConversationRequest] Failed to delete conversation:', error);
            dispatch(deleteConversationSuccessAction(conversationId));
            if (conversation) {
                const messageBoxId = getMessageBoxId(conversation.type);
                dispatch(showError(messageBoxId, 'assistant.error.deleteConversation'));
            }
        }
    };
};

export const doUpdateConversationContextRequest = (
    conversationId: string,
    context: AiAssistantConversationContext,
): TempThunkAction => {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const conversation = state.assistant.conversations[conversationId];

        if (!conversation) {
            console.warn(`Conversation ${conversationId} not found for context update`);
            return;
        }

        try {
            const conversationDto = ChatHistoryService.toConversationDto(conversation);
            await ChatHistoryService.updateConversation(conversationId, {
                ...conversationDto,
                contextSettings: JSON.stringify(context),
            });

            dispatch(updateConversationContextAction(conversationId, context));
        } catch (error) {
            console.error('Failed to update conversation context:', error);
            dispatch(updateConversationContextAction(conversationId, context));
            const messageBoxId = getMessageBoxId(conversation.type);
            dispatch(showError(messageBoxId, 'assistant.error.updateContext'));
        }
    };
};
